import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiCalendar, FiChevronLeft, FiChevronRight, FiCheck, FiX, FiCornerUpRight, FiPlus, FiMessageSquare,
  FiRefreshCw, FiRotateCcw, FiExternalLink, FiUpload, FiSend, FiEdit2,
} from 'react-icons/fi';
import { marketingAPI } from '../services/api';
import { SOURCE_COLORS } from './shared/leadSources';
import './MarketingPlan.css';

// The CMO plans and reports here instead of Word/PDF (founder, 22 Oct 2026).
// CMO / Chairman edit; CEO, COO and CSM read and comment.

const TABS = [
  { key: 'plan', label: 'Plan' },
  { key: 'weekly', label: 'Weekly report' },
  { key: 'results', label: 'Results' },
  { key: 'setup', label: 'Campaigns & series' },
];
const MAIN_PLATFORMS = ['TIKTOK', 'INSTAGRAM', 'FACEBOOK'];
const STATUS_STYLE = {
  PLANNED: { bg: '#f1f5f9', fg: '#475569' },
  DONE: { bg: '#ecfdf5', fg: '#047857' },
  MOVED: { bg: '#eff6ff', fg: '#1d4ed8' },
  DROPPED: { bg: '#fef2f2', fg: '#b91c1c' },
};
const KPI_NAMES = {
  CMO1: 'Qualified leads', CMO2: 'Cost per lead', CMO3: 'Leads with a source', CMO4: 'Plan delivered',
  CMO5: 'Engagement rate', CMO6: 'Testimonials',
};
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const parseISO = (s) => (s ? new Date(`${String(s).slice(0, 10)}T00:00:00`) : null);
const fmtDay = (s, opts = { day: 'numeric', month: 'short' }) => (s ? parseISO(s).toLocaleDateString('en-GB', opts) : '');
const fmtMonth = (key) => new Date(`${key}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
const shiftMonth = (key, n) => { const d = new Date(`${key}-01T00:00:00`); d.setMonth(d.getMonth() + n); return monthKey(d); };
const errText = (e, fallback) => {
  const d = e?.response?.data;
  if (!d) return fallback;
  if (typeof d.detail === 'string') return d.detail;
  const first = Object.entries(d)[0];
  return first ? `${first[0]}: ${[].concat(first[1]).join(' ')}` : fallback;
};
const kpiValue = (k) => {
  if (k.value == null) return '—';
  if (k.code === 'CMO2') return `$${k.value.toFixed(2)}`;
  if (['CMO3', 'CMO4', 'CMO5'].includes(k.code)) return `${k.value.toFixed(k.code === 'CMO5' ? 2 : 0)}%`;
  return String(Math.round(k.value));
};

// Where a figure came from (Meta connection, 23 Oct 2026; TikTok, 24 Oct): "Meta" / "TikTok" =
// read from the platform; "typed" = entered by hand. The platform's number wins; a typed one it
// replaced is kept.
const sourceName = (platform) => (platform === 'TIKTOK' ? 'TikTok' : 'Meta');
const SourceTag = ({ field, value, meta }) => {
  if (value === '' || value == null || !meta) return null;
  const fromMeta = (meta.meta_fields || []).includes(field);
  const typed = (meta.typed_values || {})[field];
  const source = sourceName(meta.platform);
  const title = fromMeta
    ? `From ${source}${meta.fetched_at ? `, ${new Date(meta.fetched_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}` : ''}${typed != null ? ` · you had typed ${typed}` : ''}`
    : 'Typed by hand';
  return <span className={`mp-src ${fromMeta ? 'meta' : 'typed'}`} title={title}>{fromMeta ? source : 'typed'}</span>;
};

const fmtWhen = (s) => (s ? new Date(s).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

const MetaStatus = ({ meta }) => {
  if (!meta) return null;
  if (!meta.configured) {
    return <div className="mp-meta-status off">Facebook and Instagram are not connected yet: their numbers are typed by hand.</div>;
  }
  if (meta.ok) {
    return (
      <div className="mp-meta-status ok">
        Meta connected{meta.page ? ` · ${meta.page}` : ''}{meta.instagram ? ` · @${meta.instagram}` : ''}
        {meta.last_synced_at ? ` · last synced ${fmtWhen(meta.last_synced_at)}` : ' · first read tonight'}
      </div>
    );
  }
  return (
    <div className="mp-meta-status bad">
      Meta not syncing{meta.failing_since ? ` since ${fmtWhen(meta.failing_since)}` : ''}. {meta.error}
      {meta.last_synced_at ? ` Last good read ${fmtWhen(meta.last_synced_at)}.` : ''}
    </div>
  );
};

// TikTok (brainstorms/2026-10-24-tiktok-social-connection.md): connected once by the CMO or the
// Chairman through TikTok's own sign-in; VMS never sees the TikTok password.
const TikTokStatus = ({ tiktok, canEdit, onChanged, notice }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!tiktok) return null;
  const connect = async () => {
    setBusy(true); setErr('');
    try { const { data } = await marketingAPI.tiktokConnect(); window.location.assign(data.url); } catch (e) { setErr(errText(e, 'Could not start the TikTok sign-in.')); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm('Disconnect TikTok? Its numbers will be typed by hand until someone connects again.')) return;
    setBusy(true); setErr('');
    try { await marketingAPI.tiktokDisconnect(); onChanged(); } catch (e) { setErr(errText(e, 'Could not disconnect.')); } finally { setBusy(false); }
  };
  const button = canEdit && tiktok.configured && (
    <button className="mp-btn small" disabled={busy} onClick={connect}>{tiktok.connected ? 'Connect TikTok again' : 'Connect TikTok'}</button>
  );
  let line;
  if (!tiktok.configured) {
    line = <div className="mp-meta-status off">TikTok is not connected yet: its numbers are typed by hand.</div>;
  } else if (!tiktok.connected) {
    line = <div className="mp-meta-status off">TikTok is not connected: its numbers are typed by hand.{canEdit ? " Sign in once with VEC's TikTok login." : ' The CMO or the Chairman can connect it.'} {button}</div>;
  } else if (tiktok.ok) {
    line = (
      <div className={`mp-meta-status ${tiktok.expiring_soon ? 'bad' : 'ok'}`}>
        TikTok connected{tiktok.account ? ` · @${tiktok.account}` : ''}
        {tiktok.last_synced_at ? ` · last synced ${fmtWhen(tiktok.last_synced_at)}` : ' · reading the videos now'}
        {tiktok.expires_on ? ` · sign-in runs out ${fmtDay(tiktok.expires_on, { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
        {' '}{tiktok.expiring_soon && button}
        {canEdit && <button className="mp-btn small" disabled={busy} onClick={disconnect}>Disconnect</button>}
      </div>
    );
  } else {
    line = (
      <div className="mp-meta-status bad">
        TikTok not syncing{tiktok.failing_since ? ` since ${fmtWhen(tiktok.failing_since)}` : ''}. {tiktok.error}
        {tiktok.last_synced_at ? ` Last good read ${fmtWhen(tiktok.last_synced_at)}.` : ''} {button}
      </div>
    );
  }
  return (
    <>
      {notice && <div className={`mp-meta-status ${notice.ok ? 'ok' : 'bad'}`}>{notice.text}</div>}
      {line}
      {err && <div className="mp-error">{err}</div>}
    </>
  );
};

const PlatformChips = ({ platforms, labels }) => (
  <span className="mp-chips">
    {(platforms || []).map((p) => (
      <span key={p} className="mp-chip" style={{ color: SOURCE_COLORS[p] || '#475569', borderColor: SOURCE_COLORS[p] || '#cbd5e1' }}>
        {labels[p] || p}
      </span>
    ))}
  </span>
);

const PlatformPicker = ({ value, onChange, choices }) => (
  <div className="mp-picker">
    {choices.map((c) => (
      <label key={c.value}>
        <input type="checkbox" checked={value.includes(c.value)}
          onChange={(e) => onChange(e.target.checked ? [...value, c.value] : value.filter((v) => v !== c.value))} />
        {c.label}
      </label>
    ))}
  </div>
);

// ── Item form (add or edit) ──
const ItemForm = ({ initial, weekStart, choices, campaigns, onSave, onCancel }) => {
  const [f, setF] = useState(() => ({
    title: '', planned_date: '', timing: '', pillar: '', format: 'POSTER', platforms: [...MAIN_PLATFORMS],
    campaign: '', description: '', purpose: '', ...(initial || {}),
  }));
  const [err, setErr] = useState('');
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.title.trim()) { setErr('Give the item a title.'); return; }
    const body = {
      title: f.title.trim(), planned_date: f.planned_date || null, timing: f.timing, pillar: f.pillar,
      format: f.format, platforms: f.platforms, campaign: f.campaign || null, description: f.description,
      purpose: f.purpose,
    };
    if (!f.planned_date && !initial) body.week_start = weekStart;
    try { await onSave(body); } catch (e) { setErr(errText(e, 'Could not save.')); }
  };
  return (
    <div className="mp-form">
      <div className="mp-row">
        <input className="mp-grow" placeholder="Title, e.g. Motivational Monday" value={f.title} onChange={(e) => set('title', e.target.value)} />
        <input type="date" value={f.planned_date || ''} onChange={(e) => set('planned_date', e.target.value)} title="Planned date (optional)" />
        <input placeholder="or Midweek / Weekend" value={f.timing} onChange={(e) => set('timing', e.target.value)} style={{ width: 150 }} />
      </div>
      <div className="mp-row">
        <input list="mp-pillars" placeholder="Pillar (Motivate, Showcase…)" value={f.pillar} onChange={(e) => set('pillar', e.target.value)} />
        <datalist id="mp-pillars">{choices.pillars.map((p) => <option key={p} value={p} />)}</datalist>
        <select value={f.format} onChange={(e) => set('format', e.target.value)}>
          {choices.formats.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={f.campaign || ''} onChange={(e) => set('campaign', e.target.value)}>
          <option value="">No campaign</option>
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <PlatformPicker value={f.platforms} onChange={(v) => set('platforms', v)} choices={choices.platforms} />
      <textarea rows={2} placeholder="What it is — the brief or the script" value={f.description} onChange={(e) => set('description', e.target.value)} />
      <textarea rows={2} placeholder="Purpose — why we're posting it" value={f.purpose} onChange={(e) => set('purpose', e.target.value)} />
      {err && <div className="mp-error">{err}</div>}
      <div className="mp-row">
        <button className="mp-btn primary" onClick={save}><FiCheck /> Save</button>
        <button className="mp-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
};

// ── Comments (any viewer) ──
const Comments = ({ planId, itemId }) => {
  const [rows, setRows] = useState(null);
  const [body, setBody] = useState('');
  const load = useCallback(() => {
    marketingAPI.comments(itemId ? { item: itemId } : { plan: planId }).then(({ data }) => setRows(data.results || data)).catch(() => setRows([]));
  }, [planId, itemId]);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    if (!body.trim()) return;
    await marketingAPI.addComment(itemId ? { item: itemId, body } : { plan: planId, body });
    setBody('');
    load();
  };
  return (
    <div className="mp-comments">
      {(rows || []).map((c) => (
        <div key={c.id} className="mp-comment">
          <strong>{c.author_name}</strong> <span className="mp-muted">{c.author_role} · {new Date(c.created_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</span>
          <div>{c.body}</div>
        </div>
      ))}
      {rows && rows.length === 0 && <div className="mp-muted">No comments yet.</div>}
      <div className="mp-row">
        <input className="mp-grow" placeholder="Add a comment…" value={body} onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }} />
        <button className="mp-btn" onClick={send}><FiSend /> Send</button>
      </div>
    </div>
  );
};

// ── One plan item ──
const ItemRow = ({ item, data, onChanged }) => {
  const { can_edit: canEdit, choices } = data;
  const labels = useMemo(() => Object.fromEntries(choices.platforms.map((p) => [p.value, p.label])), [choices]);
  const [mode, setMode] = useState('');
  const [links, setLinks] = useState(item.links || {});
  const [note, setNote] = useState('');
  const [moveTo, setMoveTo] = useState('');
  const [err, setErr] = useState('');
  const st = STATUS_STYLE[item.status] || STATUS_STYLE.PLANNED;

  const mark = async (body) => {
    setErr('');
    try { await marketingAPI.markItem(item.id, body); setMode(''); onChanged(); } catch (e) { setErr(errText(e, 'Could not save.')); }
  };
  const views = (item.metrics || []).reduce((a, m) => a + (m.views || 0), 0);

  return (
    <div className={`mp-item status-${item.status}`}>
      <div className="mp-item-top">
        {canEdit && item.status === 'PLANNED' ? (
          <button className="mp-tick" title="Mark done" onClick={() => setMode(mode === 'done' ? '' : 'done')}><FiCheck /></button>
        ) : (
          <span className="mp-tick static" style={{ background: st.bg, color: st.fg }}>
            {item.status === 'DONE' ? <FiCheck /> : item.status === 'DROPPED' ? <FiX /> : item.status === 'MOVED' ? <FiCornerUpRight /> : null}
          </span>
        )}
        <div className="mp-item-main">
          <div className="mp-item-title">
            {item.title}
            <span className="mp-status" style={{ background: st.bg, color: st.fg }}>{item.status_label}</span>
            {item.series_title && <span className="mp-tag">Weekly series</span>}
            {item.moved_from && <span className="mp-tag">Moved here</span>}
          </div>
          <div className="mp-item-meta">
            {item.planned_date ? fmtDay(item.planned_date, { weekday: 'short', day: 'numeric', month: 'short' }) : 'Any day'}
            {item.timing ? ` · ${item.timing}` : ''}
            {item.pillar ? ` · ${item.pillar}` : ''} · {item.format_label}
            {item.campaign_name ? ` · ${item.campaign_name}` : ''}
            {' '}<PlatformChips platforms={item.platforms} labels={labels} />
          </div>
          {item.description && <div className="mp-item-desc">{item.description}</div>}
          {item.status_note && <div className="mp-item-note">{item.status_note}</div>}
          {item.status === 'DONE' && (
            <div className="mp-item-meta">
              Done {item.done_at ? new Date(item.done_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}
              {item.done_by_name ? ` by ${item.done_by_name}` : ''}
              {views > 0 && ` · ${views} views`}
              {Object.entries(item.links || {}).map(([p, url]) => (
                <a key={p} href={url} target="_blank" rel="noreferrer" className="mp-link"><FiExternalLink /> {labels[p] || p}</a>
              ))}
            </div>
          )}
        </div>
        <div className="mp-item-actions">
          <button className="mp-icon" title="Comments" onClick={() => setMode(mode === 'comments' ? '' : 'comments')}>
            <FiMessageSquare />{item.comment_count > 0 && <span>{item.comment_count}</span>}
          </button>
          {canEdit && item.status === 'PLANNED' && (
            <>
              <button className="mp-icon" title="Edit" onClick={() => setMode(mode === 'edit' ? '' : 'edit')}><FiEdit2 /></button>
              <button className="mp-icon" title="Move to a later week" onClick={() => setMode(mode === 'move' ? '' : 'move')}><FiCornerUpRight /></button>
              <button className="mp-icon" title="Drop" onClick={() => setMode(mode === 'drop' ? '' : 'drop')}><FiX /></button>
            </>
          )}
          {canEdit && (item.status === 'DONE' || item.status === 'DROPPED') && (
            <button className="mp-icon" title="Back to planned" onClick={() => mark({ status: 'PLANNED' })}><FiRotateCcw /></button>
          )}
        </div>
      </div>
      {mode === 'done' && (
        <div className="mp-form">
          <div className="mp-muted">Post links (optional) — paste the link for each platform it went out on.</div>
          {(item.platforms.length ? item.platforms : MAIN_PLATFORMS).map((p) => (
            <div key={p} className="mp-row">
              <span style={{ width: 90 }}>{labels[p] || p}</span>
              <input className="mp-grow" placeholder="https://…" value={links[p] || ''} onChange={(e) => setLinks({ ...links, [p]: e.target.value })} />
            </div>
          ))}
          <div className="mp-row">
            <button className="mp-btn primary" onClick={() => mark({ status: 'DONE', links })}><FiCheck /> Mark done</button>
            <button className="mp-btn" onClick={() => setMode('')}>Cancel</button>
          </div>
        </div>
      )}
      {mode === 'drop' && (
        <div className="mp-form">
          <input placeholder="Why is it dropped? (required)" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="mp-row">
            <button className="mp-btn danger" onClick={() => mark({ status: 'DROPPED', note })}>Drop it</button>
            <button className="mp-btn" onClick={() => setMode('')}>Cancel</button>
          </div>
        </div>
      )}
      {mode === 'move' && (
        <div className="mp-form">
          <div className="mp-row">
            <span>Move to</span>
            <input type="date" value={moveTo} onChange={(e) => setMoveTo(e.target.value)} />
            <input className="mp-grow" placeholder="Reason (optional), e.g. Thando relocating" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="mp-row">
            <button className="mp-btn primary" onClick={() => mark({ status: 'MOVED', move_to: moveTo, note, exact_date: true })}>Move</button>
            <button className="mp-btn" onClick={() => setMode('')}>Cancel</button>
          </div>
        </div>
      )}
      {mode === 'edit' && (
        <ItemForm initial={{ ...item, campaign: item.campaign || '' }} choices={choices} campaigns={data.campaigns}
          onCancel={() => setMode('')}
          onSave={async (body) => { await marketingAPI.updateItem(item.id, body); setMode(''); onChanged(); }} />
      )}
      {mode === 'comments' && <Comments itemId={item.id} />}
      {err && <div className="mp-error">{err}</div>}
    </div>
  );
};

// ── Plan tab ──
const PlanTab = ({ data, month, onChanged }) => {
  // can_edit: work the items. full_edit: the month's goals and starting a plan (not while covering).
  const { plan, can_edit: canEdit, full_edit: fullEdit } = data;
  const [goals, setGoals] = useState({ objective: plan?.objective || '', key_goal: plan?.key_goal || '' });
  const [adding, setAdding] = useState(null);
  const [msg, setMsg] = useState('');
  useEffect(() => { setGoals({ objective: plan?.objective || '', key_goal: plan?.key_goal || '' }); }, [plan]);

  if (!plan) {
    return fullEdit ? (
      <div className="mp-card">
        <h3>Start the {fmtMonth(month)} plan</h3>
        <textarea rows={3} placeholder="Objective for the month" value={goals.objective} onChange={(e) => setGoals({ ...goals, objective: e.target.value })} />
        <textarea rows={2} placeholder="Key goal" value={goals.key_goal} onChange={(e) => setGoals({ ...goals, key_goal: e.target.value })} />
        {msg && <div className="mp-error">{msg}</div>}
        <button className="mp-btn primary" onClick={async () => {
          try { await marketingAPI.createPlan({ month, ...goals }); onChanged(); } catch (e) { setMsg(errText(e, 'Could not start the plan.')); }
        }}><FiPlus /> Start plan</button>
      </div>
    ) : <div className="mp-card mp-muted">The CMO hasn't entered the {fmtMonth(month)} plan yet.{data.stand_in ? ` Starting a plan: ${data.blocked_message}` : ''}</div>;
  }

  const byWeek = {};
  data.items.forEach((i) => { (byWeek[i.week_start] = byWeek[i.week_start] || []).push(i); });
  const p = data.progress;

  return (
    <>
      <div className="mp-card">
        <div className="mp-goals">
          <div>
            <div className="mp-label">Objective</div>
            {fullEdit ? <textarea rows={3} value={goals.objective} onChange={(e) => setGoals({ ...goals, objective: e.target.value })} />
              : <p>{plan.objective || '—'}</p>}
          </div>
          <div>
            <div className="mp-label">Key goal</div>
            {fullEdit ? <textarea rows={3} value={goals.key_goal} onChange={(e) => setGoals({ ...goals, key_goal: e.target.value })} />
              : <p>{plan.key_goal || '—'}</p>}
          </div>
        </div>
        {fullEdit && (goals.objective !== plan.objective || goals.key_goal !== plan.key_goal) && (
          <button className="mp-btn primary" onClick={async () => { await marketingAPI.updatePlan(plan.id, goals); onChanged(); }}><FiCheck /> Save</button>
        )}
      </div>

      <div className="mp-kpis">
        {data.kpis.map((k) => (
          <div key={k.code} className="mp-kpi" title={k.evidence}>
            <div className="mp-kpi-code">{k.code} · {KPI_NAMES[k.code]}</div>
            <div className="mp-kpi-value">{kpiValue(k)}</div>
            <div className="mp-kpi-evidence">{k.evidence}</div>
          </div>
        ))}
      </div>
      <div className="mp-progress">
        <div className="mp-progress-bar"><span style={{ width: `${p.done_pct || 0}%` }} /></div>
        <span>{p.counts.DONE} of {p.total} done{p.counts.MOVED ? ` · ${p.counts.MOVED} moved` : ''}{p.counts.DROPPED ? ` · ${p.counts.DROPPED} dropped` : ''}</span>
      </div>

      {data.weeks.map((w, idx) => {
        const items = byWeek[w.week_start] || [];
        return (
          <div key={w.week_start} className="mp-week">
            <div className="mp-week-head">
              <h3>Week {idx + 1} · {fmtDay(w.week_start)} – {fmtDay(w.week_end)}</h3>
              <span className={`mp-report-chip ${w.report_status || 'NONE'}`}>
                {w.report_status === 'SUBMITTED' ? 'Report submitted' : w.report_status === 'DRAFT' ? 'Report draft' : 'No report yet'}
              </span>
              {canEdit && <button className="mp-btn small" onClick={() => setAdding(adding === w.week_start ? null : w.week_start)}><FiPlus /> Add item</button>}
            </div>
            {adding === w.week_start && (
              <ItemForm weekStart={w.week_start} choices={data.choices} campaigns={data.campaigns}
                onCancel={() => setAdding(null)}
                onSave={async (body) => { await marketingAPI.createItem(body); setAdding(null); onChanged(); }} />
            )}
            {items.length === 0 && <div className="mp-muted mp-empty">Nothing planned this week.</div>}
            {items.map((i) => <ItemRow key={i.id} item={i} data={data} onChanged={onChanged} />)}
          </div>
        );
      })}

      <div className="mp-card">
        <h3>Comments on the plan</h3>
        <Comments planId={plan.id} />
      </div>
    </>
  );
};

// ── Weekly report tab ──
const SNAP_FIELDS = [
  ['followers', 'Followers'], ['views', 'Views'], ['interactions', 'Interactions'], ['new_follows', 'New follows'],
  ['profile_visits', 'Profile visits'], ['ad_spend', 'Ad spend $'],
];
const METRIC_FIELDS = [['views', 'Views'], ['likes', 'Likes'], ['comments', 'Comments'], ['shares', 'Shares']];

const WeeklyTab = ({ initialWeek, weeks }) => {
  const [week, setWeek] = useState(initialWeek);
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [metrics, setMetrics] = useState({});
  const [msg, setMsg] = useState('');
  const [attachTo, setAttachTo] = useState({});

  const load = useCallback(async () => {
    const { data } = await marketingAPI.weekly(week);
    setD(data);
    const snaps = Object.fromEntries((data.report?.snapshots || []).map((s) => [s.platform, s]));
    const platforms = [...new Set([...MAIN_PLATFORMS, ...Object.keys(snaps)])];
    setForm({
      summary: data.report?.summary || '', challenges: data.report?.challenges || '', priorities: data.report?.priorities || '',
      snapshots: platforms.map((p) => ({ platform: p, ...Object.fromEntries(SNAP_FIELDS.map(([k]) => [k, snaps[p]?.[k] ?? ''])), notes: snaps[p]?.notes || '', screenshot_url: snaps[p]?.screenshot_url, meta: snaps[p] })),
    });
    setMetrics(Object.fromEntries(data.items.map((i) => [i.id, Object.fromEntries((i.platforms || []).map((p) => {
      const m = (i.metrics || []).find((x) => x.platform === p) || {};
      return [p, Object.fromEntries(METRIC_FIELDS.map(([k]) => [k, m[k] ?? '']))];
    }))])));
  }, [week]);
  useEffect(() => { setMsg(''); load().catch(() => setMsg('Could not load the week.')); }, [load]);

  if (!d || !form) return <div className="mp-card mp-muted">Loading…</div>;
  const canEdit = d.can_edit;
  const labels = Object.fromEntries(d.choices.platforms.map((p) => [p.value, p.label]));
  const options = [...new Set([...weeks.map((w) => w.week_start), ...d.recent.map((r) => r.week_start), d.week_start])].sort().reverse();
  const setSnap = (i, k, v) => setForm((f) => ({ ...f, snapshots: f.snapshots.map((s, j) => (j === i ? { ...s, [k]: v } : s)) }));

  const save = async (submit) => {
    setMsg('');
    try {
      const { data } = await marketingAPI.saveWeekly({ week_start: d.week_start, ...form,
        snapshots: form.snapshots.filter((s) => SNAP_FIELDS.some(([k]) => s[k] !== '' && s[k] != null) || s.notes)
        .map((s) => { const row = { ...s }; delete row.meta; delete row.screenshot_url; return row; }) });
      await Promise.all(d.items.filter((i) => metrics[i.id]).map((i) => marketingAPI.itemMetrics(i.id,
        Object.entries(metrics[i.id]).map(([platform, m]) => ({ platform, ...m })))));
      if (submit) await marketingAPI.submitWeekly(data.id);
      setMsg(submit ? 'Report submitted.' : 'Saved.');
      load();
    } catch (e) { setMsg(errText(e, 'Could not save.')); }
  };
  const metricMeta = (item, platform) => (item.metrics || []).find((x) => x.platform === platform);
  const attach = async (post) => {
    const item = attachTo[post.id];
    if (!item) { setMsg('Choose the plan item this post belongs to.'); return; }
    try { await marketingAPI.attachSocialPost(post.id, item); load(); } catch (e) { setMsg(errText(e, 'Could not attach.')); }
  };
  const leaveExtra = async (post) => {
    try { await marketingAPI.extraSocialPost(post.id); load(); } catch (e) { setMsg(errText(e, 'Could not save.')); }
  };
  const upload = async (platform, file) => {
    if (!file) return;
    try {
      let id = d.report?.id;
      if (!id) { const { data } = await marketingAPI.saveWeekly({ week_start: d.week_start }); id = data.id; }
      await marketingAPI.uploadScreenshot(id, platform, file);
      load();
    } catch (e) { setMsg(errText(e, 'Upload failed.')); }
  };

  return (
    <>
      <div className="mp-row" style={{ marginBottom: 12 }}>
        <select value={d.week_start} onChange={(e) => setWeek(e.target.value)}>
          {options.map((w) => <option key={w} value={w}>Week of {fmtDay(w, { day: 'numeric', month: 'short', year: 'numeric' })}</option>)}
        </select>
        <span className="mp-muted">Monday {fmtDay(d.week_start)} – Sunday {fmtDay(d.week_end)} · Harare time</span>
        {d.report && <span className={`mp-report-chip ${d.report.status}`}>{d.report.filled_from_meta ? 'Filled from Meta' : d.report.status === 'SUBMITTED' ? `Submitted ${d.report.submitted_by_name ? `by ${d.report.submitted_by_name}` : ''}` : 'Draft'}</span>}
      </div>
      {d.report?.filled_from_meta && (
        <div className="mp-banner">A past week filled from Meta for the record: Facebook and Instagram numbers only, no CMO summary.</div>
      )}
      {d.meta?.configured && !d.meta.ok && <MetaStatus meta={d.meta} />}
      {d.tiktok?.connected && !d.tiktok.ok && <TikTokStatus tiktok={d.tiktok} canEdit={false} onChanged={() => {}} />}

      <div className="mp-card">
        <h3>Platforms this week</h3>
        <div className="mp-table-wrap">
          <table className="mp-table">
            <thead><tr><th>Platform</th>{SNAP_FIELDS.map(([, l]) => <th key={l}>{l}</th>)}<th>Rate</th><th>Screenshot</th></tr></thead>
            <tbody>
              {form.snapshots.map((s, i) => {
                const rate = s.views && s.interactions !== '' ? ((100 * s.interactions) / s.views).toFixed(2) : null;
                return (
                  <tr key={s.platform}>
                    <td><strong>{labels[s.platform] || s.platform}</strong></td>
                    {SNAP_FIELDS.map(([k]) => (
                      <td key={k}>{canEdit && !(k === 'ad_spend' && d.stand_in)
                        ? <input type="number" min="0" value={s[k]} onChange={(e) => setSnap(i, k, e.target.value)} />
                        : (s[k] === '' ? '—' : s[k])}{k !== 'ad_spend' && <SourceTag field={k} value={s[k]} meta={s.meta} />}</td>
                    ))}
                    <td>{rate ? `${rate}%` : '—'}</td>
                    <td>
                      {!s.screenshot_url && (s.meta?.meta_fields || []).length > 0 && <span className="mp-muted" title={`Read straight from ${sourceName(s.platform)}`}>From {sourceName(s.platform)}</span>}
                      {s.screenshot_url && <a href={s.screenshot_url} target="_blank" rel="noreferrer" className="mp-link"><FiExternalLink /> View</a>}
                      {canEdit && (
                        <label className="mp-upload"><FiUpload /> <input type="file" accept="image/*,.pdf" onChange={(e) => upload(s.platform, e.target.files?.[0])} /></label>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mp-muted">Interactions = likes + comments + shares + saves. Rate = interactions ÷ views (KPI CMO5). Figures tagged <em>Meta</em> or <em>TikTok</em> are read from the platform and need no screenshot; attach the insights screenshot for anything typed by hand (e.g. TikTok views and profile visits).</div>
      </div>

      <div className="mp-card">
        <h3>This week's items</h3>
        {d.items.length === 0 && <div className="mp-muted">Nothing was planned this week.</div>}
        {d.items.map((i) => (
          <div key={i.id} className="mp-metric-item">
            <div><strong>{i.title}</strong> <span className="mp-status" style={{ background: STATUS_STYLE[i.status].bg, color: STATUS_STYLE[i.status].fg }}>{i.status_label}</span></div>
            {i.status === 'DONE' && (i.platforms || []).length > 0 && (
              <div className="mp-table-wrap">
                <table className="mp-table compact">
                  <thead><tr><th>Platform</th>{METRIC_FIELDS.map(([, l]) => <th key={l}>{l}</th>)}</tr></thead>
                  <tbody>
                    {i.platforms.map((p) => (
                      <tr key={p}>
                        <td>{labels[p] || p}</td>
                        {METRIC_FIELDS.map(([k]) => (
                          <td key={k}>{canEdit ? (
                            <input type="number" min="0" value={metrics[i.id]?.[p]?.[k] ?? ''}
                              onChange={(e) => setMetrics((m) => ({ ...m, [i.id]: { ...m[i.id], [p]: { ...m[i.id]?.[p], [k]: e.target.value } } }))} />
                          ) : (metrics[i.id]?.[p]?.[k] === '' ? '—' : metrics[i.id]?.[p]?.[k])}
                          <SourceTag field={k} value={metrics[i.id]?.[p]?.[k]} meta={metricMeta(i, p)} /></td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {i.status === 'PLANNED' && <div className="mp-muted">Still planned — tick it on the Plan tab when it's out.</div>}
            {i.status_note && <div className="mp-muted">{i.status_note}</div>}
          </div>
        ))}
      </div>

      {(d.unplanned_posts || []).length > 0 && (
        <div className="mp-card">
          <h3>Posts not in the plan</h3>
          <div className="mp-muted">Read from Meta or TikTok but no plan item has their link. Attach each to its item, or leave it as an extra: extras count in engagement (CMO5), not in plan delivered (CMO4).</div>
          {d.unplanned_posts.map((post) => (
            <div key={post.id} className="mp-metric-item">
              <div>
                <strong>{labels[post.platform] || post.platform}</strong>
                {' '}<span className="mp-muted">{new Date(post.posted_at).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                {post.permalink && <a href={post.permalink} target="_blank" rel="noreferrer" className="mp-link"><FiExternalLink /> Open</a>}
              </div>
              {post.caption && <div className="mp-item-desc">{post.caption}</div>}
              <div className="mp-item-meta">
                {METRIC_FIELDS.map(([k, l]) => `${l} ${post[k] ?? '—'}`).join(' · ')}
              </div>
              {canEdit && (
                <div className="mp-row">
                  <select value={attachTo[post.id] || ''} onChange={(e) => setAttachTo({ ...attachTo, [post.id]: e.target.value })}>
                    <option value="">Belongs to…</option>
                    {(d.attach_items || []).map((it) => <option key={it.id} value={it.id}>{it.title} (week of {fmtDay(it.week_start)})</option>)}
                  </select>
                  <button className="mp-btn small primary" onClick={() => attach(post)}><FiCheck /> Attach</button>
                  <button className="mp-btn small" onClick={() => leaveExtra(post)}>Leave as extra</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mp-card">
        <h3>Summary</h3>
        {['summary', 'challenges', 'priorities'].map((k) => (
          <div key={k} style={{ marginBottom: 10 }}>
            <div className="mp-label">{k === 'summary' ? 'What happened this week' : k === 'challenges' ? 'Challenges' : 'Priorities for next week'}</div>
            {canEdit ? <textarea rows={3} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /> : <p>{form[k] || '—'}</p>}
          </div>
        ))}
        {d.next_week_items.length > 0 && (
          <div className="mp-muted">Already planned for next week: {d.next_week_items.map((i) => i.title).join(', ')}</div>
        )}
      </div>

      {msg && <div className={msg.endsWith('.') && !msg.startsWith('Could') ? 'mp-ok' : 'mp-error'}>{msg}</div>}
      {canEdit && (
        <div className="mp-row">
          <button className="mp-btn" onClick={() => save(false)}><FiCheck /> Save draft</button>
          <button className="mp-btn primary" onClick={() => save(true)}><FiSend /> Save and submit</button>
        </div>
      )}
    </>
  );
};

// ── Results tab ──
const ResultsTab = ({ data }) => {
  const total = data.leads_by_source.reduce((a, r) => a + r.count, 0);
  const max = Math.max(1, ...data.leads_by_source.map((r) => r.count));
  const labels = Object.fromEntries(data.choices.platforms.map((p) => [p.value, p.label]));
  return (
    <>
      <div className="mp-card">
        <h3>Leads by source · {total} this month</h3>
        {data.leads_by_source.length === 0 && <div className="mp-muted">No leads yet this month.</div>}
        {data.leads_by_source.map((r) => (
          <div key={r.source || 'none'} className="mp-bar-row">
            <span className="mp-bar-label">{r.label}</span>
            <span className="mp-bar"><span style={{ width: `${(100 * r.count) / max}%`, background: SOURCE_COLORS[r.source] || (r.source ? '#011ff3' : '#cbd5e1') }} /></span>
            <span className="mp-bar-value">{r.count}{r.paid ? ` (${r.paid} paid)` : ''}</span>
          </div>
        ))}
        <div className="mp-muted">Counts every new lead this month (app, website and clients added directly), leaving out spam, duplicates and "not a fit".</div>
      </div>

      <div className="mp-card">
        <h3>Sales results</h3>
        <div className="mp-table-wrap">
          <table className="mp-table">
            <thead><tr><th>Service</th><th>New this month</th><th>Signed this month</th><th>Revenue this month</th><th>Signed to date</th></tr></thead>
            <tbody>
              {data.sales.map((l) => (
                <tr key={l.key}>
                  <td title={l.signed_rule}>{l.label}</td>
                  <td>{l.new_leads_this_month}</td>
                  <td>{l.signed_this_month}</td>
                  <td>{l.revenue_this_month == null ? '—' : `$${l.revenue_this_month.toLocaleString()}`}</td>
                  <td>{l.signed_clients}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mp-card">
        <h3>Engagement by platform</h3>
        {data.engagement.by_platform.length === 0 && <div className="mp-muted">No weekly figures entered yet.</div>}
        {data.engagement.by_platform.length > 0 && (
          <div className="mp-table-wrap">
            <table className="mp-table">
              <thead><tr><th>Platform</th><th>Views</th><th>Interactions</th><th>New follows</th><th>Rate</th></tr></thead>
              <tbody>
                {data.engagement.by_platform.map((r) => (
                  <tr key={r.platform}>
                    <td>{labels[r.platform] || r.platform}</td><td>{r.views ?? '—'}</td><td>{r.interactions ?? '—'}</td>
                    <td>{r.new_follows ?? '—'}</td><td>{r.rate == null ? '—' : `${r.rate}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mp-muted">Ad spend this month: ${Number(data.spend || 0).toLocaleString()}</div>
      </div>
    </>
  );
};

// ── Campaigns & series tab ──
const CampaignForm = ({ initial, planId, choices, onDone }) => {
  const [f, setF] = useState({ name: '', objective: '', platforms: [], is_paid: false, budget: '', spend: '0', utm_code: '',
    starts_on: '', ends_on: '', status: 'PLANNED', ...(initial || {}) });
  const [err, setErr] = useState('');
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    const body = { ...f, plan: planId, budget: f.budget === '' ? null : f.budget, starts_on: f.starts_on || null, ends_on: f.ends_on || null };
    try {
      if (initial?.id) await marketingAPI.updateCampaign(initial.id, body); else await marketingAPI.createCampaign(body);
      onDone();
    } catch (e) { setErr(errText(e, 'Could not save.')); }
  };
  return (
    <div className="mp-form">
      <div className="mp-row">
        <input className="mp-grow" placeholder="Campaign name" value={f.name} onChange={(e) => set('name', e.target.value)} />
        <select value={f.status} onChange={(e) => set('status', e.target.value)}>
          {choices.campaign_statuses.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <textarea rows={2} placeholder="Objective" value={f.objective} onChange={(e) => set('objective', e.target.value)} />
      <PlatformPicker value={f.platforms} onChange={(v) => set('platforms', v)} choices={choices.platforms} />
      <div className="mp-row">
        <label><input type="checkbox" checked={f.is_paid} onChange={(e) => set('is_paid', e.target.checked)} /> Paid ads</label>
        <input type="number" min="0" placeholder="Budget $" value={f.budget ?? ''} onChange={(e) => set('budget', e.target.value)} style={{ width: 110 }} />
        <input type="number" min="0" placeholder="Spent $" value={f.spend ?? ''} onChange={(e) => set('spend', e.target.value)} style={{ width: 110 }} />
        <input type="date" value={f.starts_on || ''} onChange={(e) => set('starts_on', e.target.value)} />
        <input type="date" value={f.ends_on || ''} onChange={(e) => set('ends_on', e.target.value)} />
      </div>
      <div className="mp-row">
        <input className="mp-grow" placeholder="Link tag, e.g. tino-launch (used as utm_campaign=)" value={f.utm_code} onChange={(e) => set('utm_code', e.target.value)} />
      </div>
      {err && <div className="mp-error">{err}</div>}
      <div className="mp-row">
        <button className="mp-btn primary" onClick={save}><FiCheck /> Save</button>
        <button className="mp-btn" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
};

const SeriesForm = ({ choices, onDone }) => {
  const [f, setF] = useState({ title: '', description: '', pillar: '', format: 'POSTER', platforms: [...MAIN_PLATFORMS], weekday: 0, starts_on: '', ends_on: '' });
  const [err, setErr] = useState('');
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try { await marketingAPI.createSeries({ ...f, ends_on: f.ends_on || null }); onDone(); } catch (e) { setErr(errText(e, 'Could not save.')); }
  };
  return (
    <div className="mp-form">
      <div className="mp-row">
        <input className="mp-grow" placeholder="Series title, e.g. Motivational Monday" value={f.title} onChange={(e) => set('title', e.target.value)} />
        <select value={f.weekday} onChange={(e) => set('weekday', Number(e.target.value))}>
          {WEEKDAYS.map((d, i) => <option key={d} value={i}>Every {d}</option>)}
        </select>
      </div>
      <div className="mp-row">
        <span>From</span><input type="date" value={f.starts_on} onChange={(e) => set('starts_on', e.target.value)} />
        <span>until</span><input type="date" value={f.ends_on} onChange={(e) => set('ends_on', e.target.value)} />
        <input list="mp-pillars-s" placeholder="Pillar" value={f.pillar} onChange={(e) => set('pillar', e.target.value)} />
        <datalist id="mp-pillars-s">{choices.pillars.map((p) => <option key={p} value={p} />)}</datalist>
        <select value={f.format} onChange={(e) => set('format', e.target.value)}>
          {choices.formats.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <PlatformPicker value={f.platforms} onChange={(v) => set('platforms', v)} choices={choices.platforms} />
      <textarea rows={2} placeholder="What each edition is" value={f.description} onChange={(e) => set('description', e.target.value)} />
      {err && <div className="mp-error">{err}</div>}
      <div className="mp-row">
        <button className="mp-btn primary" onClick={save}><FiCheck /> Save series</button>
        <button className="mp-btn" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
};

const SetupTab = ({ data, onChanged, tiktokNotice }) => {
  const [editing, setEditing] = useState(null);
  const [addingSeries, setAddingSeries] = useState(false);
  // Campaigns, spend, series and TikTok stay with the CMO / Chairman while someone covers (Q4).
  const canEdit = data.full_edit;
  const labels = Object.fromEntries(data.choices.platforms.map((p) => [p.value, p.label]));
  return (
    <>
      <MetaStatus meta={data.meta} />
      <TikTokStatus tiktok={data.tiktok} canEdit={canEdit} onChanged={onChanged} notice={tiktokNotice} />
      <div className="mp-card">
        <div className="mp-week-head">
          <h3>Campaigns this month</h3>
          {canEdit && data.plan && <button className="mp-btn small" onClick={() => setEditing('new')}><FiPlus /> Add campaign</button>}
        </div>
        {!data.plan && <div className="mp-muted">Start the month's plan first.</div>}
        {editing === 'new' && <CampaignForm planId={data.plan.id} choices={data.choices} onDone={() => { setEditing(null); onChanged(); }} />}
        {data.campaigns.length === 0 && data.plan && <div className="mp-muted">No campaigns yet.</div>}
        {data.campaigns.map((c) => (editing === c.id ? (
          <CampaignForm key={c.id} initial={c} planId={data.plan.id} choices={data.choices} onDone={() => { setEditing(null); onChanged(); }} />
        ) : (
          <div key={c.id} className="mp-item">
            <div className="mp-item-top">
              <div className="mp-item-main">
                <div className="mp-item-title">{c.name} <span className="mp-tag">{c.status}</span>{c.is_paid && <span className="mp-tag">Paid</span>}</div>
                <div className="mp-item-meta">
                  {c.starts_on ? `${fmtDay(c.starts_on)} – ${fmtDay(c.ends_on) || '…'} · ` : ''}
                  Budget {c.budget == null ? '—' : `$${c.budget}`} · spent ${c.spend} · {c.lead_count} lead{c.lead_count === 1 ? '' : 's'}
                  {c.utm_code ? ` · link tag ${c.utm_code}` : ''} <PlatformChips platforms={c.platforms} labels={labels} />
                </div>
                {c.objective && <div className="mp-item-desc">{c.objective}</div>}
              </div>
              {canEdit && <div className="mp-item-actions"><button className="mp-icon" title="Edit" onClick={() => setEditing(c.id)}><FiEdit2 /></button></div>}
            </div>
          </div>
        )))}
        <div className="mp-muted">
          Tag your links so website leads land on the right platform and campaign:
          {' '}<code>visionexploit.com/?utm_source=tiktok&amp;utm_medium=social&amp;utm_campaign=tino-launch</code>
          {' '}(use <code>utm_medium=paid</code> for ads).
        </div>
      </div>

      <div className="mp-card">
        <div className="mp-week-head">
          <h3>Weekly series</h3>
          {canEdit && <button className="mp-btn small" onClick={() => setAddingSeries(true)}><FiPlus /> Add series</button>}
        </div>
        {addingSeries && <SeriesForm choices={data.choices} onDone={() => { setAddingSeries(false); onChanged(); }} />}
        {data.series.length === 0 && <div className="mp-muted">No weekly series. A series adds an item to every week of the plan by itself.</div>}
        {data.series.map((s) => (
          <div key={s.id} className="mp-item">
            <div className="mp-item-top">
              <div className="mp-item-main">
                <div className="mp-item-title">{s.title}</div>
                <div className="mp-item-meta">Every {WEEKDAYS[s.weekday]} from {fmtDay(s.starts_on)}{s.ends_on ? ` until ${fmtDay(s.ends_on)}` : ''} <PlatformChips platforms={s.platforms} labels={labels} /></div>
              </div>
              {canEdit && (
                <div className="mp-item-actions">
                  <button className="mp-btn small" onClick={async () => { if (window.confirm(`Stop "${s.title}"? Items already planned stay.`)) { await marketingAPI.stopSeries(s.id); onChanged(); } }}>Stop</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

const MarketingPlan = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  // TikTok sends the person back to /marketing/tiktok/callback?code=…&state=… after signing in.
  const [tiktokReturn, setTiktokReturn] = useState(() => (location.pathname.endsWith('/tiktok/callback')
    ? { code: params.get('code'), state: params.get('state'), error: params.get('error_description') || params.get('error') }
    : null));
  const [tiktokNotice, setTiktokNotice] = useState(tiktokReturn ? { ok: true, text: 'Connecting TikTok…' } : (location.state?.tiktokNotice || null));
  const tiktokSent = useRef(false);
  const [month, setMonth] = useState(params.get('month') || monthKey(new Date()));
  const [tab, setTab] = useState(tiktokReturn ? 'setup' : (params.get('tab') || 'plan'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: d } = await marketingAPI.overview(month);
      setData(d);
    } catch (e) {
      setError(errText(e, 'Could not load the marketing plan.'));
    } finally {
      setLoading(false);
    }
  }, [month]);
  useEffect(() => { load(); }, [load]);
  // The code is sent once (it can only be used once), then the page moves to /marketing. The
  // outcome is shown here and also carried in the navigation state, in case that route mounts a
  // fresh page instead of keeping this one.
  useEffect(() => {
    if (!tiktokReturn || tiktokSent.current) return;
    tiktokSent.current = true;
    const done = (notice) => {
      setTiktokNotice(notice);
      if (notice.ok) load();
      navigate(`/marketing?month=${month}&tab=setup`, { replace: true, state: { tiktokNotice: notice } });
      setTiktokReturn(null);
    };
    if (tiktokReturn.error || !tiktokReturn.code) {
      done({ ok: false, text: `TikTok was not connected${tiktokReturn.error ? `: ${tiktokReturn.error}` : '.'}` });
      return;
    }
    marketingAPI.tiktokCallback({ code: tiktokReturn.code, state: tiktokReturn.state })
      .then(() => done({ ok: true, text: 'TikTok connected. Its videos since 1 January are being read now; numbers appear within a few minutes.' }))
      .catch((e) => done({ ok: false, text: errText(e, 'TikTok could not be connected.') }));
  }, [tiktokReturn]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tiktokReturn) return;
    navigate(`/marketing?month=${month}&tab=${tab}`, { replace: true });  // also drops a TikTok notice already read
  }, [month, tab, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Last week's report by default (the CMO reports on Monday for the week just gone).
  const defaultWeek = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - 7 - ((d.getDay() + 6) % 7));
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  return (
    <div className="mp-page">
      <div className="mp-header">
        <div>
          <h1><FiCalendar /> Marketing Plan</h1>
          <p className="mp-muted">The month's content and campaigns, ticked as they go out, with the weekly figures, leads by source and sales results.</p>
        </div>
        <div className="mp-month">
          <button className="mp-icon" onClick={() => setMonth(shiftMonth(month, -1))} title="Previous month"><FiChevronLeft /></button>
          <strong>{fmtMonth(month)}</strong>
          <button className="mp-icon" onClick={() => setMonth(shiftMonth(month, 1))} title="Next month"><FiChevronRight /></button>
          <button className="mp-icon" onClick={load} title="Refresh"><FiRefreshCw className={loading ? 'mp-spin' : ''} /></button>
        </div>
      </div>
      <div className="mp-tabs">
        {TABS.map((t) => <button key={t.key} className={tab === t.key ? 'active' : ''} onClick={() => setTab(t.key)}>{t.label}</button>)}
      </div>
      {error && <div className="mp-error">{error}</div>}
      {data?.stand_in && (
        <div className="mp-card mp-covering">
          <strong>You're covering the CMO.</strong> You can tick items, log posts, adjust this month's plan and
          write the weekly report. Campaigns, spend, weekly series, TikTok, deleting items and other months'
          plans: {data.blocked_message}
        </div>
      )}
      {!data && loading && <div className="mp-card mp-muted">Loading…</div>}
      {data && tab === 'plan' && <PlanTab data={data} month={month} onChanged={load} />}
      {data && tab === 'weekly' && <WeeklyTab initialWeek={defaultWeek} weeks={data.weeks} />}
      {data && tab === 'results' && <ResultsTab data={data} />}
      {data && tab === 'setup' && <SetupTab data={data} onChanged={load} tiktokNotice={tiktokNotice} />}
    </div>
  );
};

export default MarketingPlan;
